import { formatPhoneDisplay } from "@taskhealth/shared";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useMemo, useState } from "react";
import { ScrollView, StyleSheet, TouchableOpacity, View } from "react-native";
import { Icon, Snackbar, Text } from "react-native-paper";
import { SafeAreaView } from "react-native-safe-area-context";

import { HtmlContent } from "@/components/HtmlContent";
import { SignatureQuestion } from "@/components/questions/SignatureQuestion";
import { YesNoQuestion } from "@/components/questions/YesNoQuestion";
import { completeSectionsBulk } from "@/lib/api";
import { useFormContext } from "@/lib/FormContext";
import { colors, fonts, spacing } from "@/lib/theme";

/**
 * Welcome Package — phase-level screen.
 * Renders all 13 sections as a single scrollable view with
 * static HTML content blocks, one confirmation checkbox, and two signatures.
 */
export default function WelcomePackageScreen() {
    const { id: visitId } = useLocalSearchParams<{ id: string }>();
    const router = useRouter();
    const { form, answers, setAnswer, flushSaves, updateForm, readOnly } = useFormContext();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Find the Welcome Package phase
    const wpPhase = useMemo(
        () => form.phases.find((p) => p.name === "Welcome Package"),
        [form.phases]
    );

    if (!wpPhase) {
        return (
            <View style={styles.center}>
                <Text>Welcome Package phase not found</Text>
            </View>
        );
    }

    // Build template variables from visit/agency/patient data
    const templateVars: Record<string, string> = {
        "{agency_name}": form.agency?.name ?? "Home Care Agency",
        "{agency_address}": form.agency?.address ?? "",
        "{agency_phone}": form.agency?.phone ? formatPhoneDisplay(form.agency.phone) : "",
        "{patient_name}": form.patient ? `${form.patient.firstName} ${form.patient.lastName}` : "",
        "{payer_source}": form.visit.payerSource ?? "N/A",
        "{service_type}": form.visit.serviceType ?? "N/A",
        "{frequency}": form.visit.frequency ?? "N/A"
    };

    function substituteVars(html: string): string {
        let result = html;
        for (const [key, val] of Object.entries(templateVars)) {
            result = result.replaceAll(key, val);
        }
        return result;
    }

    // The last section has the questions
    const lastSection = wpPhase.sections[wpPhase.sections.length - 1];
    const confirmQuestion = lastSection?.questions.find((q) => q.type === "yesNo");
    const patientSigQuestion = lastSection?.questions.find((q) => q.type === "patientSignature");
    const rnSigQuestion = lastSection?.questions.find((q) => q.type === "employeeSignature");

    const isConfirmed = confirmQuestion ? answers[confirmQuestion.id] === "Yes" : false;

    // A mandatory signature question must actually carry a value. These checks
    // existed before but were prefixed with `_` and left out of `canComplete`,
    // so Complete only ever looked at the acknowledgment — an RN could close the
    // package with her own pad untouched and the PDF printed a placeholder in
    // her signature line. The server enforces the same rule in
    // completeSectionsBulk; this is the local half so the button is honest.
    const signatureSatisfied = (question: typeof patientSigQuestion): boolean => {
        if (!question || !question.mandatory) {
            return true;
        }
        return (answers[question.id] ?? "").trim().length > 0;
    };

    const hasPatientSig = signatureSatisfied(patientSigQuestion);
    const hasRnSig = signatureSatisfied(rnSigQuestion);
    const canComplete = isConfirmed && hasPatientSig && hasRnSig;

    // Named so the RN knows which pad is still empty rather than staring at a
    // greyed-out button.
    const missingLabels = [
        !isConfirmed ? "the acknowledgment" : null,
        !hasPatientSig ? (patientSigQuestion?.text ?? "Patient Signature") : null,
        !hasRnSig ? (rnSigQuestion?.text ?? "Registered Nurse Signature") : null
    ].filter((label): label is string => label !== null);

    const allSectionsCompleted = wpPhase.sections.every((sec) =>
        form.sectionStatuses.some(
            (ss) => String(ss.sectionId) === String(sec.id) && ss.status === "completed"
        )
    );

    const handleComplete = async () => {
        if (!visitId || !canComplete) {
            return;
        }
        setLoading(true);
        try {
            // Flush any pending answer saves
            await flushSaves();

            // Complete all 13 sections in a single request
            await completeSectionsBulk(
                visitId,
                wpPhase.sections.map((s) => String(s.id))
            );

            // Update local form state
            updateForm((prev) => ({
                ...prev,
                sectionStatuses: [
                    ...prev.sectionStatuses.filter(
                        (ss) => !wpPhase.sections.some((s) => String(s.id) === String(ss.sectionId))
                    ),
                    ...wpPhase.sections.map((sec) => ({
                        id: `wp-${sec.id}`,
                        visitId: visitId!,
                        sectionId: String(sec.id),
                        status: "completed" as const,
                        completedAt: new Date().toISOString()
                    }))
                ]
            }));

            router.back();
        } catch (e) {
            setError(e instanceof Error ? e.message : "Failed to complete");
        } finally {
            setLoading(false);
        }
    };

    return (
        <SafeAreaView style={styles.container} edges={["top"]}>
            {/* Same affordance the Assessment shows. Without it the signature pads
                are simply inert before clock-in, which reads as a broken app
                rather than a deliberate lock. */}
            {readOnly ? (
                <View style={styles.banner}>
                    <Text style={styles.bannerText}>
                        {form.visit.status === "assigned"
                            ? "Preview — clock in to sign the welcome package"
                            : "Visit completed — view only"}
                    </Text>
                </View>
            ) : null}

            <ScrollView contentContainerStyle={styles.scroll}>
                {/* Back button */}
                <TouchableOpacity
                    style={styles.backRow}
                    onPress={() => (router.canGoBack() ? router.back() : router.replace("/"))}
                >
                    <Icon source="arrow-left" size={20} color={colors.textSecondary} />
                    <Text style={styles.backText}>Back</Text>
                </TouchableOpacity>

                {/* Title */}
                <View style={styles.titleCard}>
                    <Icon source="file-document-outline" size={28} color={colors.primary} />
                    <View style={{ flex: 1 }}>
                        <Text style={styles.titleText}>Welcome Package</Text>
                        <Text style={styles.titleSub}>
                            {wpPhase.sections.length} documents to review
                        </Text>
                    </View>
                    {allSectionsCompleted && (
                        <Icon source="check-circle" size={24} color={colors.success} />
                    )}
                </View>

                {/* Section cards with static content */}
                {wpPhase.sections.map((section, idx) => (
                    <View key={section.id} style={styles.sectionCard}>
                        <View style={styles.sectionHeader}>
                            <Text style={styles.sectionNumber}>{idx + 1}</Text>
                            <Text style={styles.sectionName}>
                                {section.displayName ?? section.name}
                            </Text>
                        </View>
                        {section.staticContentBlocks.map((block) => (
                            <View key={block.id} style={styles.contentBlock}>
                                <HtmlContent html={substituteVars(block.contentHtml)} />
                            </View>
                        ))}
                    </View>
                ))}

                {/* Acknowledgment + Signatures */}
                {!allSectionsCompleted && (
                    <View style={styles.signatureCard}>
                        <Text style={styles.signatureTitle}>Acknowledgment & Signatures</Text>

                        {confirmQuestion && (
                            <View style={styles.questionRow}>
                                <YesNoQuestion
                                    question={confirmQuestion}
                                    value={answers[confirmQuestion.id] ?? ""}
                                    onChange={(val) => setAnswer(String(confirmQuestion.id), val)}
                                    readOnly={readOnly}
                                />
                            </View>
                        )}

                        {patientSigQuestion && (
                            <View style={styles.questionRow}>
                                <Text style={styles.sigLabel}>
                                    {patientSigQuestion.text}
                                    {patientSigQuestion.mandatory && (
                                        <Text style={styles.required}> *</Text>
                                    )}
                                </Text>
                                <SignatureQuestion
                                    question={patientSigQuestion}
                                    value={answers[patientSigQuestion.id] ?? ""}
                                    onChange={(val) =>
                                        setAnswer(String(patientSigQuestion.id), val)
                                    }
                                    readOnly={readOnly}
                                />
                            </View>
                        )}

                        {rnSigQuestion && (
                            <View style={styles.questionRow}>
                                <Text style={styles.sigLabel}>
                                    {rnSigQuestion.text}
                                    {rnSigQuestion.mandatory && (
                                        <Text style={styles.required}> *</Text>
                                    )}
                                </Text>
                                <SignatureQuestion
                                    question={rnSigQuestion}
                                    value={answers[rnSigQuestion.id] ?? ""}
                                    onChange={(val) => setAnswer(String(rnSigQuestion.id), val)}
                                    readOnly={readOnly}
                                />
                            </View>
                        )}

                        {missingLabels.length > 0 && (
                            <Text style={styles.missingHint}>
                                Still needed: {missingLabels.join(", ")}
                            </Text>
                        )}

                        <TouchableOpacity
                            style={[styles.completeBtn, !canComplete && styles.completeBtnDisabled]}
                            onPress={handleComplete}
                            disabled={!canComplete || loading}
                            activeOpacity={0.8}
                        >
                            <Icon
                                source="check-bold"
                                size={20}
                                color={canComplete ? "#FFFFFF" : colors.textTertiary}
                            />
                            <Text
                                style={[
                                    styles.completeBtnText,
                                    !canComplete && styles.completeBtnTextDisabled
                                ]}
                            >
                                {loading ? "Completing..." : "Complete Welcome Package"}
                            </Text>
                        </TouchableOpacity>
                    </View>
                )}

                {allSectionsCompleted && (
                    <View style={styles.completedBanner}>
                        <Icon source="check-circle" size={32} color={colors.success} />
                        <Text style={styles.completedText}>Welcome Package completed</Text>
                    </View>
                )}
            </ScrollView>

            <Snackbar
                visible={!!error}
                onDismiss={() => setError(null)}
                duration={4000}
                style={styles.snackbar}
            >
                {error}
            </Snackbar>
        </SafeAreaView>
    );
}

const styles = StyleSheet.create({
    banner: {
        backgroundColor: colors.warning50,
        paddingVertical: spacing.sm,
        paddingHorizontal: spacing.base,
        borderBottomWidth: 1,
        borderBottomColor: colors.warning400
    },
    bannerText: {
        fontFamily: fonts.semiBold,
        fontSize: 13,
        color: colors.textSecondary,
        textAlign: "center"
    },
    container: {
        flex: 1,
        backgroundColor: colors.backgroundSecondary
    },
    scroll: {
        padding: 16,
        paddingBottom: 40
    },
    center: {
        flex: 1,
        alignItems: "center",
        justifyContent: "center"
    },
    backRow: {
        flexDirection: "row",
        alignItems: "center",
        marginBottom: 12,
        gap: 6
    },
    backText: {
        fontSize: 15,
        color: colors.textSecondary
    },

    // Title card
    titleCard: {
        flexDirection: "row",
        alignItems: "center",
        gap: 12,
        backgroundColor: colors.background,
        borderRadius: 16,
        padding: 20,
        marginBottom: 16,
        borderWidth: 1,
        borderColor: colors.surfaceBorder
    },
    titleText: {
        fontSize: 20,
        fontWeight: "800",
        color: colors.textPrimary
    },
    titleSub: {
        fontSize: 13,
        color: colors.textSecondary,
        marginTop: 2
    },

    // Section cards
    sectionCard: {
        backgroundColor: colors.background,
        borderRadius: 12,
        marginBottom: 12,
        borderWidth: 1,
        borderColor: colors.surfaceBorder,
        overflow: "hidden"
    },
    sectionHeader: {
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        backgroundColor: colors.primaryLight,
        paddingVertical: 10,
        paddingHorizontal: 16,
        borderBottomWidth: 1,
        borderBottomColor: colors.surfaceBorder
    },
    sectionNumber: {
        fontSize: 12,
        fontWeight: "700",
        color: colors.primary,
        backgroundColor: colors.background,
        width: 24,
        height: 24,
        borderRadius: 12,
        textAlign: "center",
        lineHeight: 24,
        overflow: "hidden"
    },
    sectionName: {
        fontSize: 15,
        fontWeight: "700",
        color: colors.textPrimary,
        flex: 1
    },
    contentBlock: {
        padding: 16
    },

    // Signature card
    signatureCard: {
        backgroundColor: colors.background,
        borderRadius: 16,
        padding: 20,
        marginTop: 8,
        borderWidth: 1,
        borderColor: colors.surfaceBorder
    },
    signatureTitle: {
        fontSize: 18,
        fontWeight: "700",
        color: colors.textPrimary,
        marginBottom: 16
    },
    questionRow: {
        marginBottom: 16
    },
    sigLabel: {
        fontSize: 14,
        fontWeight: "600",
        color: colors.textPrimary,
        marginBottom: 8
    },
    required: {
        color: colors.error
    },
    missingHint: {
        fontSize: 13,
        color: colors.textSecondary,
        marginTop: 4,
        marginBottom: 8,
        lineHeight: 18
    },

    // Complete button
    completeBtn: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        backgroundColor: colors.primary,
        borderRadius: 14,
        paddingVertical: 16,
        marginTop: 8
    },
    completeBtnDisabled: {
        backgroundColor: colors.backgroundSecondary,
        borderWidth: 1,
        borderColor: colors.surfaceBorder
    },
    completeBtnText: {
        fontSize: 17,
        fontWeight: "700",
        color: "#FFFFFF"
    },
    completeBtnTextDisabled: {
        color: colors.textTertiary
    },

    // Completed banner
    completedBanner: {
        alignItems: "center",
        backgroundColor: colors.successLight,
        borderRadius: 16,
        paddingVertical: 24,
        marginTop: 8,
        gap: 8
    },
    completedText: {
        fontSize: 16,
        fontWeight: "600",
        color: colors.success
    },

    snackbar: {
        backgroundColor: colors.textPrimary
    }
});
